import React from 'react';
import { vars } from '@croquiscom-pvt/zds/tokens';
import { css } from '@emotion/react';
import styled from '@emotion/styled';

import { BundlePurchaseItemList } from '@common/checkout-utils';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { Tooltip } from '@domains/pdp-common';

import { ConfirmButtons } from '../../components/Confirm/ConfirmButtons';
import { PriceSummaryRow } from '../../components/Confirm/PriceSummaryRow';
import { PriceTotalRow } from '../../components/Confirm/PriceTotalRow';
import { ShippingFee } from '../../components/Confirm/ShippingFee';
import { LegacyRow as Row } from '../../components/layouts/LegacyRow';
import { ZigzinProductFreeShippingAmount } from '../../constant';
import { useConfirmService } from '../../services/useConfirmService';

interface ConfirmProps {
  isLoggedIn: boolean;
  onClose?: () => void;
  onAddCart?: (bundle_purchase_item_list: BundlePurchaseItemList) => void;
  onEditClick?: () => void;
  isEditable?: boolean;
  isEditDisabled?: boolean;
  isCouponUnused?: boolean;
  disabledButton?: boolean;
}

export const ConfirmContainer: React.FC<React.PropsWithChildren<ConfirmProps>> = ({
  isLoggedIn,
  onClose,
  onAddCart,
  onEditClick,
  isEditable,
  isEditDisabled,
  isCouponUnused,
  disabledButton,
}) => {
  const service = useConfirmService({ isLoggedIn, onClose, onAddCart, isCouponUnused });
  const visibleButton = !service._isPreview && !disabledButton;

  function handleEditClick() {
    service.editUbl();

    onEditClick?.();
  }

  return (
    <Row css={checkout}>
      {service.hasCouponDiscount && (
        <Summary>
          <PriceSummaryRow label='총 금액' price={service.totalInfo.price} />
          {!isCouponUnused && (
            <PriceSummaryRow
              label='쿠폰 할인'
              prefixPrice='-'
              price={service.couponDiscountAmount}
              priceColor={service.discountColor}
              style={{ marginTop: 8 }}
              tooltip={
                <>
                  <Dimmed useTouch={service.couponTooltip} onClick={() => service.toggleCouponTooltip()} />
                  <Tooltip
                    display={service.couponTooltip}
                    animate={true}
                    top='-64px'
                    left='-53px'
                    tipLeft='54px'
                    whiteSpace='pre'
                    text={'결제 시에만 적용 가능한 쿠폰이 있을 경우,\n쿠폰 할인 금액이 지금보다 클 수 있습니다.'}
                    arrowPosition={{ position: 'left', direction: 'down' }}
                  />
                </>
              }
              onClickInfo={() => service.toggleCouponTooltip()}
            />
          )}
        </Summary>
      )}

      <PriceTotalRow
        discountText={service.finalDiscountInfo}
        label='결제 예상 금액'
        price={service.finalPrice}
        priceColor={service.finalPriceColor}
      />

      {!service.isDigitalShipping && (
        <Row className='shipping_fee_row'>
          <ShippingFee
            shippingFee={service.shippingFee}
            matchingShippingFee={service.matching_catalog_product_info?.shipping_fee}
            totalPrice={service.totalInfo.price}
            generalTotalPrice={service.totalInfo.generalPrice}
            isZigzinProduct={service.isZigzinProduct}
            isMatchingProduct={Boolean(service.matching_catalog_product_info)}
            isZigzinProductFreeShipping={
              !service.isDeliveryTomorrow ? service.zigzinProductFinalPrice >= ZigzinProductFreeShippingAmount : true
            }
            isMatchingAll={service.isMatchingAll}
            hasMatchingZigzin={service.hasMatchingZigzin}
            hasMatchingGeneral={service.hasMatchingGeneral}
          />
        </Row>
      )}

      {visibleButton && (
        <Row className='btn_set'>
          {
            <ConfirmButtons
              buttonTypes={{ ...service.confirmButtons.buttonTypes, isEditable }}
              actions={{
                ...service.confirmButtons.actions,
                onEditClick: handleEditClick,
              }}
              editState={{
                isEditDisabled,
              }}
            />
          }
        </Row>
      )}
    </Row>
  );
};

const checkout = css`
  padding: 13px 16px 0 16px;
  border-top: 1px solid ${vars.v3Color.gray100};

  .shipping_fee_row {
    margin: 6px 0 0 0;
    display: flex;
    align-items: flex-start;
    justify-content: flex-end;
    height: 17px;
    font-size: 14px;
    font-weight: 400;
    color: ${vars.v3Color.gray500};
  }

  .btn_set {
    margin: 28px 0 0 0;
    display: flex;
    justify-content: center;
    column-gap: 7px;
    height: 61px;

    > .btn {
      max-width: 100%;
      width: 100%;
      height: 50px;
      border-width: 1px;

      span {
        font-weight: 600;
      }
    }
  }
`;

const Summary = styled.div`
  margin: 0 0 10px 0;
  padding: 0 0 12px 0;
  border-bottom: 1px solid ${vars.v3Color.gray100};
`;

const Dimmed = styled.div<{ useTouch: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 999;
  pointer-events: ${({ useTouch }) => (useTouch ? 'auto' : 'none')};
`;
